//
// Copyright (c) 2011-2024 Curalate, Inc.
//

import { isDefined, isNotNull } from '@utils/filterPredicates';
import parseUrl from '@utils/parseUrl';

export default function getUrlParams(
  queryString?: string,
): Record<string, string> {
  const searchParams = new URLSearchParams(
    queryString ?? window.location.search,
  );

  return Object.fromEntries(searchParams);
}

/**
 * Adds a query parameter, or replaces the current one if it exists.
 * @param url A url to add the query param to
 */
export function setQueryParam(
  url: string,
  key: string,
  value?: string,
): string {
  const newUrl = new URL(url);

  if (isDefined(value) && isNotNull(value)) {
    newUrl.searchParams.set(key, value);
  } else {
    newUrl.searchParams.delete(key);
  }

  return newUrl.href;
}

/**
 * Returns the domain from url
 *
 * Favor `getUrlHost` instead
 *
 * @param url
 * @param preserveSubdomain
 */
export function getDomainFromUrl(
  url: string,
  preserveSubdomain?: boolean,
): string {
  return (
    (() => {
      const hostname = /([-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6})/gi.exec(
        url,
      )?.[1];
      if (preserveSubdomain) {
        return hostname;
      } else {
        return (
          hostname && /.*?([-a-zA-Z0-9]+\.[-a-zA-Z0-9]+)$/gi.exec(hostname)?.[1]
        );
      }
    })() ?? url
  );
}

/**
 * Removes protocols from url (including secure and relative)
 * Will optionally remove 'www.' from the beginning of the url
 *
 * Note: expected that url is validated before using
 *
 * @param url
 * @param shouldRemoveWWW
 */
export function getUrlWithoutProtocol(url: string, shouldRemoveWWW = false) {
  const urlRegex = shouldRemoveWWW
    ? new RegExp(`^${URL_PROTOCOL_PATTERN_WITH_WWW}`, 'i')
    : new RegExp(`^${URL_PROTOCOL_PATTERN_WITHOUT_WWW}`, 'i');
  return url.replace(urlRegex, '');
}

/**
 * Adds 'https://' secure protocol to url - will replace any existing protocol
 *
 * Note: expected that url is validated before using
 *
 * @param url
 */
export function getUrlWithSecureProtocol(url: string) {
  const urlWithoutProtocol = getUrlWithoutProtocol(url);

  return `https://${urlWithoutProtocol}`;
}

/**
 * Returns the host string from a url
 *
 * Note: expected that url is validated before using
 *
 * @param url
 */
export function getUrlHost(url: string) {
  // Add a protocol first to prevent accidentally returning host from a relative url
  const urlWithProtocol = getUrlWithSecureProtocol(url);

  return parseUrl(urlWithProtocol).host;
}

// escapes are needed for regex
// eslint-disable-next-line no-useless-escape
export const URL_PROTOCOL_PATTERN_WITH_WWW = '(?:https?://|//)?(?:www.)?';

// escapes are needed for regex
// eslint-disable-next-line no-useless-escape
export const URL_PROTOCOL_PATTERN_WITHOUT_WWW = '(?:https?://|//)?';
